import { Observable } from 'rxjs';
import { isArray, size } from 'lodash';

import { BULK_REQUEST_COUNT } from '@config/global-constants';

import {
  IApiListResponse,
  IBaseEntityType,
  IBohApiListResponse,
  IFilterQuery,
} from '../types';
import { rx } from '../rx';
import { observableFrom, observableOf } from '../rx/rx-wrapper';

/**
 * you can use this operator to batch request your requests
 *
 * CAUTION: remember to reduce all results by your self after using this operator
 *
 * @param documents based on documents
 * @param predict request predict
 */
export const batchRequestMap = <T = IBaseEntityType, R = IBaseEntityType>(
  documents: T[],
  predict: (document: T) => Observable<R>,
  batchCount: number = BULK_REQUEST_COUNT,
): Observable<R> =>
  observableFrom(documents).pipe(
    rx.bufferCount(batchCount),
    rx.concatMap((bufferedDocuments: IBaseEntityType[]) =>
      observableFrom(bufferedDocuments).pipe(rx.mergeMap(predict)),
    ),
  );

export type ApiListResponse<T = IBaseEntityType> =
  | IApiListResponse<T>
  | IBohApiListResponse<T>;

export const defaultBatchMapAccumulatedPredict = <T = IBaseEntityType>(
  accumulatedRes: ApiListResponse<T>,
  newRes: ApiListResponse<T>,
): IApiListResponse<T> => {
  const accumulatedItems =
    defaultApiListResponseNormalizer(accumulatedRes)?.items;
  const newItems = defaultApiListResponseNormalizer(newRes)?.items;

  const mergedItems = [...(accumulatedItems ?? []), ...(newItems ?? [])];
  const mergedTotals = size(mergedItems);

  return {
    items: mergedItems,
    page_size: mergedTotals,
    total: mergedTotals,
    current_page: 1,
    page_count: 1,
  };
};

/**
 * batch map for array with accumulated result predict
 *
 * @param accumulatedRes
 * @param newRes
 * @returns
 */
export const defaultBatchArrayMapAccumulatedPredict = <T = IBaseEntityType>(
  accumulatedRes: T[],
  newRes: T[],
): T[] => {
  return [...(accumulatedRes ?? []), ...(newRes ?? [])];
};

export const defaultApiListResponseNormalizer = <T = IBaseEntityType>(
  response: ApiListResponse<T>,
  params?: {
    pageNumber?: number;
    top?: number;
  },
): IApiListResponse<T> => {
  if ('items' in response && 'total' in response) {
    return response as IApiListResponse<T>;
  }

  // for compatibility with Array Response
  const items = isArray(response)
    ? response
    : (response as IBohApiListResponse<T>)?.Data;
  const pageNumber = params?.pageNumber ?? 0;
  const total =
    (response as IBohApiListResponse<T>)?.TotalCount ?? size(items) ?? 0;

  const countedPageCount =
    total !== 0 && pageNumber !== 0 ? Math.ceil(total / pageNumber) : 0;

  return {
    items,
    total,
    current_page: pageNumber <= 0 ? 1 : pageNumber,
    page_count: pageNumber <= 0 ? 1 : countedPageCount,
    page_size: size(items),
  } as IApiListResponse<T>;
};

const defaultContinuePredict = <T = IBaseEntityType>(
  response: ApiListResponse<T>,
  accumulatedRes: ApiListResponse<T>,
) => {
  const currentPageTotal = size(
    (response as IApiListResponse<T>)?.items ??
      (response as IBohApiListResponse<T>)?.Data,
  );

  const accumulatedItemsCount = size(
    (accumulatedRes as IApiListResponse<T>)?.items ??
      (accumulatedRes as IBohApiListResponse<T>)?.Data,
  );

  const totalCount =
    (response as IApiListResponse<T>)?.total ??
    (response as IBohApiListResponse<T>)?.TotalCount;

  return (
    currentPageTotal > 0 &&
    currentPageTotal + accumulatedItemsCount < totalCount
  );
};

const defaultNextParamsPredict = <
  T = IBaseEntityType,
  P extends {
    filter?: IFilterQuery;
  } = {
    filter?: IFilterQuery;
  },
>(
  response: IApiListResponse<T>,
  currentParams: P,
): P => {
  return {
    ...currentParams,
    current_page: response.current_page + 1,
  };
};

const defaultAccumulatedPredict = <T = IBaseEntityType>(
  accumulatedRes: IApiListResponse<T>,
  newRes: IApiListResponse<T>,
): IApiListResponse<T> => {
  return {
    ...accumulatedRes,
    items: [...(accumulatedRes?.items || []), ...(newRes?.items || [])],
    page_size: newRes.page_size,
    total: newRes.total,
    current_page: newRes.current_page,
    page_count: newRes.page_count,
  };
};

/**
 * batch request while xxxx
 *
 * u can use it to batch request with pagination until all data are fetched
 *
 * @param options
 * @returns
 */
export const batchRequestWhile = <
  P extends {
    filter?: IFilterQuery;
    top?: number;
    pageNumber?: number;
    itemPerPage?: number;
  } = {
    filter?: IFilterQuery;
    top?: number;
    pageNumber?: number;
    itemPerPage?: number;
  },
  E = IBaseEntityType,
  R extends IApiListResponse<E> = IApiListResponse<E>,
>(options: {
  initParams?: {
    filter?: IFilterQuery;
    top?: number;
    pageNumber?: number;
  };
  /**
   * request predict function
   */
  requestPredict: (params: P) => Observable<R>;
  /**
   * response normalizer or mapper function
   * use it to normalize the response to IApiListResponse<T> or your custom logics
   */
  responseNormalizer?: (response: any, params?: P) => R;
  /**
   * judge whether to continue the next request
   */
  continuePredict?: (response: R, accumulatedRes: R) => boolean;
  /**
   * next params predict function
   * u can use it to set the next page params
   */
  nextParamsPredict?: (response: R, currentParams: P) => P;
  /**
   * accumulated result predict function
   */
  accumulatedPredict?: (accumulatedRes: R, newRes: R) => R;
}) => {
  const {
    requestPredict,
    responseNormalizer = defaultApiListResponseNormalizer,
    continuePredict = defaultContinuePredict,
    nextParamsPredict = defaultNextParamsPredict,
    accumulatedPredict = defaultAccumulatedPredict,
    initParams = {},
  } = options;

  const initState: {
    params: P;
    first: boolean;
    response: R;
    accumulatedRes: R;
  } = {
    params: initParams as P,
    first: true,
    response: null,
    accumulatedRes: null,
  };

  return observableOf(initState).pipe(
    rx.expand(
      (state: {
        params: P;
        first: boolean;
        response: R;
        accumulatedRes: R;
        shouldContinue: boolean;
      }) => {
        if (!state.first && !state.shouldContinue) {
          return observableOf(state);
        }

        return requestPredict(state.params).pipe(
          rx.map((response) => responseNormalizer(response, state.params) as R),
          rx.map((response) => {
            const shouldContinue = continuePredict(
              response,
              state.accumulatedRes,
            );
            const accumulatedRes = accumulatedPredict(
              state.accumulatedRes,
              response,
            );

            return {
              ...state,
              shouldContinue,
              params: shouldContinue
                ? nextParamsPredict?.(response, state.params) ?? state.params
                : state.params,
              accumulatedRes: (shouldContinue
                ? accumulatedRes
                : {
                    // finalize the accumulated result(reset pagination info)
                    ...accumulatedRes,
                    page_size: size(accumulatedRes.items),
                    current_page: 1,
                    page_count: 1,
                  }) as R,
              first: false,
            };
          }),
        );
      },
    ),
    rx.takeWhile((state) => state.first || state.shouldContinue, true),
    rx.last(),
    rx.map((state) => state.accumulatedRes),
  );
};
